// @vendors
import { useState } from "react";
import {
  Bars3Icon,
  Squares2X2Icon,
  UserIcon,
  ChatBubbleLeftIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  HeartIcon,
  Cog6ToothIcon,
  SunIcon,
  ArrowLeftStartOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { MoonIcon } from "@heroicons/react/24/solid";

// @components
import { NavItem } from "../molecules/NavItem";
import Modal from "../../components/Modals/ModalErrorValidation";

// @store
import { useSidebarStore } from "../../store/useSidebarStore";
import useAuthStore from "../../store/useAuthStore";
import { useThemeStore } from "../../store/useThemeStore";
import { useCompanyStore } from "../../store/useCompanyStore";
import { useLogoutStore } from "../../store/useLogoutStore";

// @helpers
import routesConfig from "../../config/routes.config";
import { getNameUser } from "../../helpers/functionsUtils";

// @assets
import avatar from "../../assets/icons/avatar.svg";
import iconClose from "../../assets/icons/icon_close.svg";

export const SidebarDashboard = () => {
  const { t } = useTranslation();
  const { isExpanded, toggleSidebar } = useSidebarStore();
  const { roleList } = useAuthStore();
  const { isDarkMode, toggleDarkMode } = useThemeStore();
  const [openModal, setOpenModal] = useState(false);
  //const [openMenu, setOpenmenu] = useState(false);
  const { data, userData } = useCompanyStore();
  const companyData = data;
  const { handleLogout } = useLogoutStore();

  const navItems = [
    {
      href: routesConfig.dashboardProfessional,
      icon: <Squares2X2Icon />,
      label: t("professional.drawer.dashboard"),
    },
    {
      href: routesConfig.userProfessional,
      icon: <UserIcon />,
      label: t("professional.drawer.user"),
    },
    {
      href: routesConfig.chatProfessional,
      icon: <ChatBubbleLeftIcon />,
      label: t("professional.drawer.messages"),
    },
    {
      href: routesConfig.servicesProfessional,
      icon: <BriefcaseIcon />,
      label: t("professional.drawer.services"),
    },
    {
      href: routesConfig.proposalsProfessional,
      icon: <DocumentTextIcon />,
      label: t("professional.drawer.proposals"),
    },
    {
      href: routesConfig.marketplaceProfessional,
      icon: <ShoppingCartIcon />,
      label: t("professional.drawer.marketplace"),
    },
    {
      href: routesConfig.paymentsProfessional,
      icon: <HeartIcon />,
      label: t("professional.drawer.paymentHistory"),
    },
    {
      href: routesConfig.configProfessional,
      icon: <Cog6ToothIcon />,
      label: t("professional.drawer.configuration"),
    },
  ];

  const handleOpenModal = () => setOpenModal(true);

  return (
    <aside
      className={`
        sticky left-0 lg:top-0 top-[0px] h-[70px] lg:h-screen bg-white dark:bg-dark-cBackground border-r border-gray-200 dark:border-none lg:p-4 p-0
        transition-all duration-300
        ${isExpanded ? "w-full lg:w-64" : "w-full lg:w-20"}
      `}
    >
      <div className={`flex lg:flex-col flex-row h-[70px] lg:h-full items-center`}>
        <div className={`flex flex-row gap-1 items-center justify-between lg:w-full ${isExpanded ? "ml-2 w-[20%]" : " w-[10%]"}`}>
          {isExpanded && (
            <>
              {/* Logo */}
              <div className="lg:mb-8 mb-0 ">
                <img src="/logo512.png" alt="Logo" className="lg:h-12 h-8" />
              </div>
            </>
          )}

          {/* Toggle Button */}
          <button
            onClick={toggleSidebar}
            className="lg:mb-8 mb-0 lg:p-2 p-0 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg self-end"
          >
            <Bars3Icon className="lg:w-6 lg:h-6 w-[24px] h-[24px] dark:text-white" />
          </button>
        </div>

        {/* Navigation */}
        <nav
          className={`flex flex-row lg:flex-col lg:space-y-1 space-y-0 space-x-0 lg:w-full w-[75%] justify-center items-center  ${isExpanded
              ? "flex flex-col lg:flex-row items-start mt-[300px] lg:mt-0 w-[100%] "
              : "flex flex-row lg:flex-col "
            }`}
        >
          {navItems.map((item, index) => (
            <NavItem
              key={index}
              {...item}
              isDashboard={!companyData?.isActive ? true : false}
              onClick={handleOpenModal}
              active={window.location.pathname === item.href}
            />
          ))}
        </nav>



        {/* Bottom Section */}
        <div className={`flex flex-row lg:flex-col border-t border-gray-200 lg:pt-4 pt-0 lg:w-full  ${isExpanded ? "mr-2 w-[40%]" : " w-[15%]"} items-center justify-between`}>
          <NavItem
            icon={
              isDarkMode ? (
                <SunIcon className="size-4 lg:size-7" />
              ) : (
                <MoonIcon className="size-5 lg:size-7" />
              )
            }
            label={
              !isDarkMode
                ? t("professional.drawer.darkMode")
                : t("professional.drawer.lightMode")
            }
            onClick={toggleDarkMode}
          />

          {/* Profile */}
          {isExpanded ? (
            <div
              className={`flex items-center gap-3 lg:px-0 py-3 mt-2 lg:w-full justify-between  ${isExpanded ? "justify-between" : "justify-between"
                }`}
            >
              <img
                src={avatar}
                alt="Profile"
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white text-center">
                  {getNameUser(roleList, data, userData)}
                </p>
              </div>
              <button
                onClick={() => handleLogout(true)}
                className="text-sm text-red-600 hover:text-red-700 flex items-center gap-2"
              >
                <ArrowLeftStartOnRectangleIcon className="size-6" />
              </button>
            </div>
          ) : (
            <NavItem
              onClick={() => handleLogout(true)}
              icon={<ArrowLeftStartOnRectangleIcon />}
              className="text-red-600 hover:text-red-700"
            />
          )}
        </div>
      </div>
      {openModal && (
        <Modal
          imageUrl={iconClose}
          title={t("professional.drawer.titleModal")}
          message={t("professional.drawer.textModal")}
          buttonText={t("professional.acceptButton")}
          onClose={() => setOpenModal(false)}
          widthModal="w-[40%]"
        />
      )}
    </aside>
  );
};
